import React, { useMemo, useState } from "react";
import { Button, Divider, Space, Typography, notification, theme } from "antd";
import { NewCorpEntity } from "./corp-new-entity";
import ProCard from "@ant-design/pro-card";
import Context from "@ant-design/icons/lib/components/Context";
import { CorporationOperation } from "../../serviceoperation/CorporationOperation";

interface AddCorpSummaryFormProps {
  newCorpData: NewCorpEntity;
  onCreate: (values: NewCorpEntity, response: any) => void;
}

const CorpSummary: React.FC<AddCorpSummaryFormProps> = ({
  newCorpData,
  onCreate,
}) => {
  const { useToken } = theme;
  const { token } = useToken();
  const [loading, setLoading] = useState<boolean>(false);
  const [api, contextHolder] = notification.useNotification();
  const corpOps = useMemo(() => new CorporationOperation(), []);
  const { Title, Paragraph, Text } = Typography;

  const processCompanyCreation = async () => {
    setLoading(true);

    // Process Insert Company Update
    // So that we can process the Awesomeness.
    corpOps
      .postCorp({
        name: newCorpData.name!,
        description: newCorpData.description,
        website: newCorpData.website,
        address: {
          addressLine1: newCorpData.address!.addressLine1!,
          addressLine2: newCorpData.address?.addressLine2,
          addressLine3: undefined,
          city: newCorpData.address!.city!,
          state: newCorpData.address!.state!,
          zip: newCorpData.address!.zip!,
          country: "US",
        },
        timeZoneId: newCorpData.timeZoneId!,
        contact: {
          firstName: "Nothing",
          lastName: "ToSeeHere",
          email: newCorpData.contact!.email,
        },
        metadata: Object.keys(newCorpData.metadata!).map((key) => {
          const metadataID: number = parseInt(key, 10);
          const metadataValue: string = newCorpData.metadata![key];
          return {
            metadataID,
            metadataValue,
          };
        }),
        branch: newCorpData!.branch!.map((x) => ({
          name: x.name,
          branchCode: "str",
          description: x.description,
          address: {
            addressLine1: x.addressLine1!,
            addressLine2: x.addressLine2,
            addressLine3: undefined,
            city: x.city!,
            state: x.state!,
            zip: x.zip!,
            country: "US",
          },
          timeZoneId: x.timeZoneId!,
          department: x.departments?.map((dep) => ({
            name: dep.name,
            description: dep.description,
          })),
        })),
        timOffReasons: [
          {
            name: "Personal Time Off",
            description: "Personal time off.",
          },
          {
            name: "Bereavement",
            description:
              "Bereavement leave is paid leave which is available to an employee at the time of death or funeral of a member of the employee's immediate family.",
          },
          {
            name: "Other",
            description: "Other reason that can't be categorized.",
          },
        ],
      })
      .then((x) => {
        //console.log(x);
        if (x.statusCode === 201) {
          api.success({
            message: `Thank you!`,
            description: (
              <Context.Consumer>
                {() =>
                  `We've successfully processed your request, please stay on this page while we redirect you!`
                }
              </Context.Consumer>
            ),
          });
          // Introduce a 5-second delay before calling onCreate
          // Such a Placebo effect :P
          setTimeout(() => {
            onCreate(newCorpData, x);
          }, 5000); // 5000 milliseconds = 5 seconds
        } else {
          api.error({
            message: `Oops!`,
            description: (
              <Context.Consumer>
                {() =>
                  `We're sorry for the inconvenience, but we're currently facing some issues processing your request. Please try again later or feel free to contact us for assistance.`
                }
              </Context.Consumer>
            ),
          });
          setLoading(false);
        }
      });
  };

  return (
    <div style={{ padding: "40px" }}>
      <Title level={3}>You're almost done!</Title>
      <Paragraph>
        Let's pause for a moment to carefully review your inputs. Once you're
        satisfied that everything is in order, just a click away lies the power
        to create your organization.
      </Paragraph>

      <ProCard>
        <Title level={5}>Corporation Info</Title>
        <Space direction="vertical" size="small" style={{ marginBottom: 20 }}>
          <Paragraph>
            <Text strong>Company Name:</Text> {newCorpData.name}
          </Paragraph>
          <Paragraph>
            <Text strong>Description:</Text> {newCorpData.description || "-"}
          </Paragraph>
          <Paragraph>
            <Text strong>Website:</Text> {newCorpData.website || "-"}
          </Paragraph>
          <Paragraph>
            <Text strong>Time Zone:</Text> {newCorpData.timeZoneId}
          </Paragraph>

          <Title level={5}>Address</Title>
          {newCorpData.address ? (
            <Space direction="vertical" size="small">
              <Paragraph>{newCorpData.address.addressLine1}</Paragraph>
              {newCorpData.address.addressLine2 && (
                <Paragraph>{newCorpData.address.addressLine2}</Paragraph>
              )}
              <Paragraph>
                {newCorpData.address.city}, {newCorpData.address.state}{" "}
                {newCorpData.address.zip}
              </Paragraph>
              <Paragraph>{"US"}</Paragraph>
            </Space>
          ) : (
            <Paragraph>No address provided</Paragraph>
          )}

          <Divider />

          <Title level={5}>Branches</Title>

          {newCorpData!.branch!.map((branch) => (
            <div
              key={branch.name}
              style={{
                marginBottom: 20,
                paddingLeft: 12,
                borderLeft: "2px solid #ccc",
              }}
            >
              <Paragraph>
                <Text strong>Name:</Text> {branch.name}
              </Paragraph>
              <Paragraph>
                <Text strong>Description:</Text> {branch.description || "-"}
              </Paragraph>
              <Title level={5}>Address</Title>
              <Paragraph>{branch.addressLine1}</Paragraph>
              {branch.addressLine2 && (
                <Paragraph>{branch.addressLine2}</Paragraph>
              )}
              <Paragraph>
                {branch.city}, {branch.state} {branch.zip}
              </Paragraph>
              <Paragraph>{"US"}</Paragraph>
              <Paragraph>
                <Text strong>Time Zone:</Text> {branch.timeZoneId}
              </Paragraph>

              <Title level={5}>Departments</Title>
              {branch.departments.map((dep) => (
                <Paragraph key={dep.name} style={{ paddingLeft: 20 }}>
                  <Text strong>{dep.name}:</Text> {dep.description || "-"}
                </Paragraph>
              ))}
            </div>
          ))}

          <Divider />

          <Title level={5}>Time Off Reasons</Title>
          {[
            {
              name: "Personal Time Off",
              description: "Personal time off.",
            },
            {
              name: "Bereavement",
              description:
                "Bereavement leave is paid leave which is available to an employee at the time of death or funeral of a member of the employee's immediate family.",
            },
            {
              name: "Other",
              description: "Other reason that can't be categorized.",
            },
          ].map((reason) => (
            <Paragraph key={reason.name}>
              <Text strong>{reason.name}:</Text> {reason.description}
            </Paragraph>
          ))}
        </Space>
      </ProCard>

      {contextHolder}
      <Button
        loading={loading}
        type="primary"
        style={{ marginTop: 24 }}
        onClick={processCompanyCreation}
      >
        Looks Good!
      </Button>
    </div>
  );
};

export default CorpSummary;
